
import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../../context/ToastContext';
import { AdminService } from '../../../application/services/AdminService';

const MedidorForm = ({ onClose, onSuccess, initialData, empresas = [], isSuperAdmin = false, currentUserEmpresaId = null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const isEditing = !!initialData;
    const toast = useToast();

    // Form state
    const [formData, setFormData] = useState({
        codigo_medidor: '',
        nombre_medidor: '',
        ubicacion_medidor: '',
        fecha_instalacion: '',
        estado_medidor: 'activo',
        id_cliente: '',
        id_tarifa: '',
        es_comunal: false,
    });

    // Client search - search by name from all clients
    const [clientSearch, setClientSearch] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientError, setClientError] = useState('');
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [showClientResults, setShowClientResults] = useState(false);

    // Tarifas list
    const [tarifas, setTarifas] = useState([]);

    useEffect(() => {
        // Fetch tarifas and clients on mount
        const loadData = async () => {
            try {
                const tarifasData = await AdminService.getAllTarifas();
                setTarifas(tarifasData || []);
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        if (initialData) {
            setFormData({
                codigo_medidor: initialData.codigo_medidor || '',
                nombre_medidor: initialData.nombre_medidor || '',
                ubicacion_medidor: initialData.ubicacion_medidor || '',
                fecha_instalacion: initialData.fecha_instalacion || '',
                estado_medidor: initialData.estado_medidor || 'activo',
                id_cliente: initialData.id_cliente || '',
                id_tarifa: initialData.id_tarifa || '',
                es_comunal: initialData.es_comunal || false,
            });
            // Pre-fill client name if available
            if (initialData.cliente) {
                setSelectedClient({
                    id_cliente: initialData.id_cliente,
                    nombres: initialData.cliente.nombres,
                    apellidos: initialData.cliente.apellidos,
                    nombre_cliente: `${initialData.cliente.nombres} ${initialData.cliente.apellidos}`.trim(),
                    codigo_cliente: initialData.cliente.codigo_cliente
                });
            }
        }
    }, [initialData, isSuperAdmin]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleClientSearch = async () => {
        if (!clientSearch.trim()) {
            setClientError('Ingrese un nombre de cliente');
            setFilteredClientes([]);
            return;
        }
        setClientError('');

        try {
            const response = await AdminService.searchClientes(clientSearch);
            const matches = response.items || response || [];

            if (matches.length === 0) {
                setClientError('No se encontraron clientes');
                setFilteredClientes([]);
            } else {
                setFilteredClientes(matches);
                setShowClientResults(true);
            }
        } catch (error) {
            console.error('Error searching clients:', error);
            setClientError('Error al buscar clientes');
        }
    };

    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setFormData(prev => ({ 
            ...prev, 
            id_cliente: client.id_cliente,
        }));
        const fullName = `${client.nombres} ${client.apellidos}`.trim();
        setClientSearch(fullName || client.nombre_cliente);
        setShowClientResults(false);
        setFilteredClientes([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Validation
        if (!formData.codigo_medidor) {
            toast.error('El código del medidor es requerido');
            setIsLoading(false);
            return;
        }

        if (!formData.id_cliente) {
            toast.error('Debe seleccionar un cliente');
            setIsLoading(false);
            return;
        }



        try {
            if (isEditing) {
                await AdminService.updateMedidor(initialData.codigo_medidor, formData);
                toast.success('Medidor actualizado exitosamente');
            } else {
                await AdminService.createMedidor(formData);
                toast.success('Medidor creado exitosamente');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving medidor:', error);
            toast.error(typeof error === 'string' ? error : 'Error al guardar el medidor');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditing ? 'Editar Medidor' : 'Nuevo Medidor'}
            size="lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isLoading}>
                        Guardar
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                


                {/* Client Search by Name */}
                <div className="border-b pb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buscar Cliente por Nombre</label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="Ingrese nombre del cliente"
                            value={clientSearch}
                            onChange={(e) => {
                                setClientSearch(e.target.value);
                                setClientError('');
                                setShowClientResults(false);
                            }}
                        />
                        <Button type="button" variant="secondary" onClick={handleClientSearch}>
                            Buscar
                        </Button>
                    </div>
                    {clientError && <p className="text-red-500 text-sm mt-1">{clientError}</p>}
                    
                    {/* Search Results Dropdown */}
                    {showClientResults && filteredClientes.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-sm">
                            {filteredClientes.map(client => (
                                <div
                                    key={client.id_cliente}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                    onClick={() => handleSelectClient(client)}
                                >
                                    <p className="text-sm font-medium text-gray-800">
                                        {client.nombres} {client.apellidos}
                                    </p>
                                    <p className="text-xs text-gray-500">Cédula: {client.codigo_cliente}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {selectedClient && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-800">
                                <strong>Cliente Seleccionado:</strong> {selectedClient.nombres} {selectedClient.apellidos}
                                <span className="text-gray-500 ml-2">({selectedClient.codigo_cliente})</span>
                            </p>
                        </div>
                    )}
                </div>

                {/* Main fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                        label="Código del Medidor"
                        value={formData.codigo_medidor}
                        onChange={e => handleChange('codigo_medidor', e.target.value)}
                        required
                        disabled={isEditing}
                    />
                    <Input
                        label="Nombre del Medidor"
                        value={formData.nombre_medidor}
                        onChange={e => handleChange('nombre_medidor', e.target.value)}
                    />
                    <Input
                        label="Ubicación"
                        value={formData.ubicacion_medidor}
                        onChange={e => handleChange('ubicacion_medidor', e.target.value)}
                    />
                    <Input
                        label="Fecha de Instalación"
                        type="date"
                        value={formData.fecha_instalacion}
                        onChange={e => handleChange('fecha_instalacion', e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tarifa Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Tarifa</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                            value={formData.id_tarifa}
                            onChange={e => handleChange('id_tarifa', e.target.value)}
                        >
                            <option value="">Seleccione tarifa...</option>
                            {tarifas.map(tarifa => (
                                <option key={tarifa.id_tarifa} value={tarifa.id_tarifa}>
                                    {tarifa.nombre_aplicacion} - {tarifa.tipo_tarifa}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Estado Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Estado del Medidor</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                            value={formData.estado_medidor}
                            onChange={e => handleChange('estado_medidor', e.target.value)}
                        >
                            <option value="activo">Activo</option>
                            <option value="suspendido">Suspendido</option>
                        </select>
                    </div>
                </div>

                {/* Exento de Pago Toggle */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <input
                        id="es_comunal"
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        checked={formData.es_comunal}
                        onChange={e => handleChange('es_comunal', e.target.checked)}
                    />
                    <label htmlFor="es_comunal" className="text-sm font-medium text-blue-900 cursor-pointer">
                        Uso Comunal / Exento de Pago
                    </label>
                    <span className="text-xs text-blue-600 block ml-auto italic">
                        * No genera deudas ni mora
                    </span>
                </div>
            </form>
        </Modal>
    );
};

export default MedidorForm;
